import { currentQuestion, currentAnswer, createAnswerObject, findQuestion } from "./questionStates";
import { cloneDeep, clone, isEmpty, has, get, each, keys, reverse } from "lodash";

import strings from "../resources/strings";

const pemHeader = "-----BEGIN PRIVATE KEY-----";
const pemFooter = "-----END PRIVATE KEY-----";

const strToArrayBuffer = function(str) {
    const buf = new ArrayBuffer(str.length);
    const bufView = new Uint8Array(buf);
    for (let i = 0, strLen = str.length; i < strLen; i++) {
      bufView[i] = str.charCodeAt(i);
    }
    return buf;
};

const arrayBufferToStr = (arrayBuffer) => {
    const byteArray = new Uint8Array(arrayBuffer);
    let byteString = '';
    for(var i=0; i < byteArray.byteLength; i++) {
        byteString += String.fromCodePoint(byteArray[i]);
    }
    return byteString;
}


const assignQuestionValue = (question, value) => {
    if (!question.hasOwnProperty("value")) {
        return question;
    }

    let newQuestion = question.slice();
    newQuestion.value = value;

    return newQuestion;
};

const assignFormField = (question, answer, state, field, value) => {
    if (isEmpty(answer)) {
        answer = createAnswerObject(question, state);
    }

    const anwserFields = answer.fields;
    const currentField = get(anwserFields.filter(f => f.fieldID === field), "0");
    if (!currentField) {
        return;
    }

    const fieldPos = anwserFields.indexOf(currentField);

    // handle fieldType of 'array', convert elements to text fields, then remove array field from answerFields
    if (currentField.fieldType === "array") {
        const fieldValues = Array.isArray(value) ? value : [value];
        each(fieldValues, (fieldValue, index) => {
            // reverse array preserver order of key pairs using splice()
            const fieldValueKeys = reverse(keys(fieldValue));
            each(fieldValueKeys, fieldValueKey => {
                const fieldID = `${currentField.fieldID}${fieldValueKey}-${index}`;
                const newField = {
                    fieldID: fieldID,
                    fieldType: "text",
                    label: fieldValueKey,
                    value: fieldValue[fieldValueKey]
                };
                // add new field to answerFields
                anwserFields.splice(fieldPos, 0, newField);
            });
        });
        // finally remove the original array field after replacement
        anwserFields.splice(anwserFields.indexOf(currentField), 1);
    }
    // handle all other fields
    else {
        anwserFields[fieldPos].value = value;
    }

    // handle fields marked as needing to be signed
    if (get(currentField, "sign", false)) {
        const storedPrivateKey = localStorage.getItem("attestivPRIVATEKey");
        if (!storedPrivateKey) {
            console.error(`Private key not found to sign field named: ${currentField.fieldID}`);
            return;
        }
        
        const pemContents = storedPrivateKey.substring(pemHeader.length, storedPrivateKey.length - pemFooter.length);
      
        window.crypto.subtle.importKey("pkcs8", strToArrayBuffer(window.atob(pemContents)), {name: "RSASSA-PKCS1-v1_5", hash: "SHA-256"}, true, ["sign"])
        .then(function(privateKey) {
            let dataToBeSigned = strToArrayBuffer(value);
            window.crypto.subtle.sign("RSASSA-PKCS1-v1_5", privateKey, dataToBeSigned)
            .then(function(signature) {
                anwserFields[fieldPos].signedValue = arrayBufferToStr(dataToBeSigned);
                anwserFields[fieldPos].signature = arrayBufferToStr(signature);
            }).catch(function(err) {
                console.log(err);
            });
        })
        .catch(function(err) {
            console.error(err);
        });        
    }
    
    return answer;
};

const assignFromCamera = (state, expectedFields, returnedFields) => {
    let question = currentQuestion(state);
    let answer = currentAnswer(state);
    let forwardAnswers = has(state, "forwardAnswers") ? state.forwardAnswers : {};

    console.debug("Assigning from camera");
    for (var f in returnedFields) {
        console.debug("Populating field: " + f + " with value: " + returnedFields[f]);
        if (expectedFields.hasOwnProperty(f)) {
            let fieldPath = expectedFields[f].split(".");
            if (fieldPath.length < 1) {
                answer = assignQuestionValue(question, returnedFields[f]);
            }
            else {
                let questionID = fieldPath[0];
                let fieldID = fieldPath[1];
                let ans;

                if (isEmpty(answer) || answer.questionID !== questionID) {
                    ans = forwardAnswers[questionID];
                    if (!ans) {
                        let q = findQuestion(state, questionID);
                        if (q) {
                            ans = createAnswerObject(q, state);
                            if (answer.questionID !== questionID) {
                                forwardAnswers[questionID] = ans;
                            }
                        }
                        else {
                            console.error(strings.getString("invalidPathForField", expectedFields[f]));
                            continue;
                        }
                    }
                }
                else {
                    ans = answer;
                }

                answer = assignFormField(question, ans, state, fieldID, returnedFields[f]);
            }
        }
    }

    return {
        ...state,
        forwardAnswers: forwardAnswers
    };
};

const assignFromUrlQueryString = (state, action) => {
    let userdata = action.data;
    if (isEmpty(userdata)) {
        return;
    }

    let forwardAnswers = has(state, "forwardAnswers") ? state.forwardAnswers : {};    
    let urlPrefillFields = has(state, "urlPrefillFields") ? cloneDeep(state.urlPrefillFields) : {};
    let parts = userdata.split(",");
    each(parts, (part) => {
        const trimmedPart = part.trim();
        const separatorIndex = trimmedPart.indexOf("=");
        if (separatorIndex <= 0) {
            console.error(strings.getString("invalidUserDataUrlParamFormat", part));
            return;
        }

        let name = trimmedPart.substring(0, separatorIndex);
        let value = trimmedPart.substring(separatorIndex + 1);
        let nameParts = name.trim().split(".");
        if (nameParts.length < 2) {
            console.error(strings.getString("invalidUserDataUrlParamFormat", name));
            return;
        }

        let questionID = nameParts[0];
        let fieldID = nameParts[1];

        if (!urlPrefillFields[questionID]) {
            urlPrefillFields[questionID] = [];
        }
        if (!urlPrefillFields[questionID].includes(fieldID)) {
            urlPrefillFields[questionID].push(fieldID);
        }

        let ans = forwardAnswers[questionID];
        let q = findQuestion(state, questionID);
        if (!ans) {
            if (q) {
                ans = createAnswerObject(q, state);
                forwardAnswers[questionID] = ans;
            }
            else {
                console.error(strings.getString("invalidPathForField", questionID));
                return;
            }
        }

        assignFormField(q, ans, state, fieldID, value);
    });

    return {
        ...state,
        forwardAnswers: forwardAnswers,
        urlPrefillFields: urlPrefillFields
    }
};

// Parse the json tables returned by Weber
// Returns lists of the columns and values for use by react-table
const parseTable = tableJSON => {
    const headers = [];
    const data = [];
    const columnMap = {}; // Keep track of the columns by index

    tableJSON.forEach((row, rowIndex) => {
        // Assume the first row is the headers
        if (rowIndex === 0) {
            row.forEach((col, colIndex) => {
                if (!isEmpty(col)) {
                    let colName = col[0];
                    headers.push({ Header: colName, accessor: colName });
                    columnMap[colIndex] = colName;
                }
            });
        } else {
            // Grab the relevant columns from this row
            let rowData = {};
            for (const colIndex in columnMap) {
                let colName = columnMap[colIndex];
                rowData[colName] = row[colIndex].join(" "); // Row elements are arrays of words
            }

            data.push(rowData);
        }
    });

    return { columns: headers, data: data };
};

const assignTables = (state, returnedTables) => {
    let answerCopy = cloneDeep(currentAnswer(state));

    // All the expectedFields are in place from the old assignFromCamera
    // Just pop in the tables if we find them
    for (let tableName in returnedTables) {
        let answerFilter = answerCopy.fields.filter(a => a.fieldID === tableName);
        if (answerFilter.length > 0) {
            let thisAnswerField = answerFilter[0];

            const parsedTable = parseTable(returnedTables[tableName]);
            thisAnswerField["columns"] = parsedTable["columns"];
            thisAnswerField["data"] = parsedTable["data"];
        }
    }

    let questionHistory = clone(state.questionHistory);
    questionHistory[state.currentAnswerIndex] = answerCopy;

    return {
        ...state,
        questionHistory: questionHistory
    };
};

const assignConfidences = (state, confidences) => {
    let answerCopy = cloneDeep(currentAnswer(state));
    console.log(JSON.stringify(confidences));

    for (let i = 0; i < answerCopy.fields.length; i++) {
        let confidence = confidences[answerCopy.fields[i].fieldID];
        if (confidence) {
            answerCopy.fields[i].confidence = confidence;
        }
    }

    let questionHistory = clone(state.questionHistory);
    questionHistory[state.currentAnswerIndex] = answerCopy;

    return {
        ...state,
        questionHistory: questionHistory
    };
};

// The new weber API returns fields AND tables
const assignFromCameraNew = (state, cameraFields, cameraResults) => {
    // First, assign fields as before
    // This will copy all the question's fields into the answer
    let newState = assignFromCamera(state, cameraFields, cameraResults.fields);

    newState = assignConfidences(newState, cameraResults.confidences);

    // Take the updated state and assign any tables
    return assignTables(newState, cameraResults.tables);
};

// for assiging extracted text, currently only support docType: "prebuilt:receipt"
// documentResults is an array with the following fields:
// - ReceiptType: confidence, type, valueString
// - Items.valueArray has elements
//   - valueObject:
//     - Name: confidence, page, text, type="text" and valueString
//     - Quantity: confidence, page, text, type="number" and valueNumber
//     - TotalPrice: confidence, page, text, type="number" and valueNumber
// - MerchantAddress: confidence, page, text, type="text", valueString
// - MerchantName: confidence, page, text, type="text", valueString
// - MerchantPhoneNumber: confidence, page, text, type="text", valueString
// - Subtotal: confidence, page, text, type="number", valueNumber
// - Tax: confidence, page, text, type="number", valueNumber
// - Total: confidence, page, text, type="number", valueNumber
// - TransactionDate: confidence, page, text, type="date", valueDate
// - TransactionTime: confidence, page, text, type="time", valueTime
const assignFromExtractedText = (state, action) => {
    const cameraFields = action.cameraFields;
    const cameraResults = action.cameraResults;
    const newState = assignFromCamera(state, cameraFields, cameraResults);
    let answerCopy = cloneDeep(currentAnswer(newState));

    let questionHistory = clone(state.questionHistory);
    questionHistory[state.currentAnswerIndex] = answerCopy;

    return {
        ...newState,
        questionHistory: questionHistory
    };
}

const dispatchAssignment = (state, action) => {
    let question = currentQuestion(state);
    let answer = currentAnswer(state);
    let result;

    if (question.questionType === "form") {
        result = assignFormField(question, answer, state, action.field, action.value);
    } else {
        result = assignQuestionValue(question, action.value);
    }

    let questionHistory = clone(state.questionHistory);
    questionHistory[state.currentAnswerIndex] = result;

    return {
        ...state,
        questionHistory: questionHistory
    };
};

export {
    assignFromCamera,
    assignFromCameraNew,
    assignFromExtractedText,
    assignFromUrlQueryString,
    dispatchAssignment
};
